import { MQ_AND_BELOW } from "@customTypes/Breakpoints";
import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { ProgrammeType } from "@customTypes/ProgrammeType";
import { fetchApi } from "@utilities/fetchApi";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";
import { Pagination } from "swiper/modules";
import { Swiper as Slider, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/pagination";
import styles from "./C272_ProgrammeComparisonTable.module.scss";
import { ProgrammeDE } from "./C272_ProgrammeDE";
import { ProgrammeEE } from "./C272_ProgrammeEE";

export interface ProgrammeComparisonTableProps {
  programmeType: ProgrammeType;
}

export const ProgrammeComparisonTable = (props: {
  content: ProgrammeComparisonTableProps;
}) => {
  const { content } = props;
  const { programmeType } = content;
  const [programmes, setProgrammes] = useState<ReactElement[]>([]);
  const [programmeDetails, setProgrammeDetails] = useState<ProgrammeDetails[]>(
    []
  );

  const { query } = useRouter();
  const device = useDevice();
  const isMobile = device === "mobile";
  const [useCarousel, setUseCarousel] = useState(isMobile);

  useEffect(() => {
    if (!query || !query.p) return;

    const typeCodes = query.p
      ? [...new Set((query.p as string).split(","))]
      : [];

    if (!typeCodes.length) return;

    const getData = async () => {
      const programmeDetails = await fetchApi(
        "/api/getProgrammeComparisonData",
        { programmeType, typeCodes }
      );

      setProgrammeDetails(programmeDetails);
    };
    getData();

    setTimeout(() => {
      adjustHeight();
    }, 500);

    setUseCarousel(window.innerWidth <= MQ_AND_BELOW.MOBILE);

    window.addEventListener("resize", () => {
      adjustHeight();

      setUseCarousel(window.innerWidth <= MQ_AND_BELOW.MOBILE);
    });
  }, [query]);

  useEffect(() => {
    if (!programmeDetails || !programmeDetails.length) {
      setProgrammes([]);
      return;
    }

    const programmes = programmeDetails.map((programmeDetail, index) => {
      const Programme =
        programmeType === ProgrammeType.DE ? ProgrammeDE : ProgrammeEE;
      programmeDetail.slug = programmeDetail.externalUrl
        ? programmeDetail.externalUrl
        : programmeDetail.slug;
      return useCarousel ? (
        <SwiperSlide key={`programme-table-${index}`}>
          <Programme count={index + 1} programmeDetails={programmeDetail} />
        </SwiperSlide>
      ) : (
        <Programme
          key={`programme-table-${index}`}
          count={index + 1}
          programmeDetails={programmeDetail}
        />
      );
    });

    setProgrammes(programmes);
  }, [programmeDetails, useCarousel]);

  const adjustHeight = () => {
    setTimeout(function () {
      // Side labels
      const sideLabels = [
        ...document.querySelectorAll(
          ".programme-items .labels [data-reference]"
        ),
      ];

      // All cells within table
      const tableCells = [
        ...document.querySelectorAll<HTMLElement>(
          ".programme-column [data-reference]"
        ),
      ];

      // Table heading
      const tableHeadings = [
        ...document.querySelectorAll<HTMLElement>(
          ".programme-column .top-heading"
        ),
      ];

      // set all table cell height to auto
      for (let i = 0; i <= tableCells.length; i++) {
        if (tableCells[i]) {
          tableCells[i].style.height = "auto";
        }
      }

      let titleHeight = 0;

      // Get of of all title heading and set titleHeight to highest value
      for (let i = 0; i <= tableHeadings.length; i++) {
        if (tableHeadings[i]) {
          if (tableHeadings[i].offsetHeight > titleHeight) {
            titleHeight = tableHeadings[i].offsetHeight;
          }
        }
      }

      // Set height of title headings
      for (let i = 0; i <= tableHeadings.length; i++) {
        if (tableHeadings[i]) {
          tableHeadings[i].style.height = titleHeight + "px";
        }
      }

      // loop through all side labels
      for (let i = 0; i < sideLabels.length; i++) {
        const filteredTarget = [];
        let tallestCellHeight = 0;
        const sideLabelDataVal = sideLabels[i].getAttribute("data-reference");

        // loop through all table cells
        for (let index = 0; index < tableCells.length; index++) {
          const element = tableCells[index];

          // Match side label data-reference value with table cell data-reference
          if (element.getAttribute("data-reference") == sideLabelDataVal) {
            const currectCellHeight = element.offsetHeight;

            // get tallest cell height
            if (currectCellHeight > tallestCellHeight) {
              tallestCellHeight = currectCellHeight;
            }

            // push all matching items into array
            filteredTarget.push(element);
          }

          // loop through filteredTarget items and apply height value
          for (let index = 0; index < filteredTarget.length; index++) {
            const element = filteredTarget[index];
            element.style.height = tallestCellHeight + "px";
          }
        }
      }
    }, 0);
  };

  const sliderSettings = {
    slidesPerView: 1,
    allowTouchMove: true,
    pagination: {
      clickable: true,
    },
    modules: [Pagination],
    className: clsx(styles.slider, "programme-comparison-table", "swiper-pb-0"),
    speed: 500,
  };

  if (!programmes) return null;

  return (
    <div
      className={clsx(
        "component",
        styles["programme-comparison-table"],
        "wrapper"
      )}
    >
      <div className="items-wrapper">
        <div className={clsx(styles["programme-items"], "programme-items")}>
          <ul
            id="programme-column-wrapper"
            data-block_length={
              useCarousel ? "" : programmeDetails && programmeDetails.length
            }
          >
            {useCarousel ? (
              <Slider {...sliderSettings}>{programmes}</Slider>
            ) : (
              programmes
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProgrammeComparisonTable;
